import { faBookmark as faBookmarkSolid } from '@fortawesome/free-solid-svg-icons';
import {
  faBookmark,
  faCameraSlash,
  faExpand,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { t } from 'i18next';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Carousel, Col, Container, Row } from 'react-bootstrap';
import { SIZING_MATRIX_ICONS } from '../../../helpers/fontawesome';
import { combine } from '../../../helpers/styles';
import { useJuror } from '../../../hooks/useAuth';
import Submission from '../../../models/submission';
import Button, { ButtonSizes, ButtonTypes } from '../../button';
import FullScreenImage from '../entryFullScreenImage';
import styles from './styles.module.scss';

const EntryDetailHeader = ({ submission, handleSubmissionUpdate }) => {
  const [bookmarkHover, setBookmarkHover] = useState(false);
  const { jurorGroup, session, isPreview } = useJuror();
  const [fullScreen, setFullscreen] = useState(false);
  const [images, setImages] = useState(submission?.fields?.images || []);

  const handleBookmark = () => {
    return Submission.setBookmark(
      !submission.bookmarked,
      jurorGroup.jurorGroupId,
      submission.submissionId
    ).then((isOk) => {
      if (isOk) {
        handleSubmissionUpdate({
          ...submission,
          bookmarked: !submission.bookmarked,
        });
      }
    });
  };

  const closeFullScreen = () => {
    setFullscreen(false);
  };

  return (
    <>
      {fullScreen && (
        <FullScreenImage
          image={fullScreen}
          onClose={closeFullScreen}
          carousel={images}
        />
      )}
      <Container fluid>
        <Container className={styles.entryContainer}>
          <div className={styles.entryInformation}>
            <div className={styles.entryInformationLeft}>
              <span className={styles.entryId}>
                {t('evaluation.EntryID')}{' '}
                <span className={styles.bold}>{submission?.submissionId}</span>
              </span>
              <h1 className={styles.title}>{submission?.submissionName}</h1>
              <h2 className={styles.subtitle}>
                {submission?.fields.entryType}
              </h2>
            </div>
            <div className={styles.entryInformationRight}>
              <span className={styles.entryId}>
                {t(`evaluation.Section.${session?.awardType}`)}{' '}
                <span className={styles.bold}>{submission?.categoryName}</span>
              </span>
              {!isPreview && (
                <FontAwesomeIcon
                  onMouseEnter={() => setBookmarkHover(true)}
                  onMouseLeave={() => setBookmarkHover(false)}
                  onClick={handleBookmark}
                  role="button"
                  color="#ed1d26"
                  size="2x"
                  className={combine(styles.bookmark)}
                  icon={
                    submission?.bookmarked || bookmarkHover
                      ? faBookmarkSolid
                      : faBookmark
                  }
                />
              )}
            </div>
          </div>
        </Container>
        <Container className={styles.entryMain}>
          <Row>
            <Col sm={12} lg={8}>
              <Carousel
                touch
                indicators={!!submission?.submissionPrimaryMediaUrl}
                controls={!!submission?.submissionPrimaryMediaUrl}
                className={styles.entryCarousel}
                interval={null}
              >
                {!submission?.submissionPrimaryMediaUrl ? (
                  <Carousel.Item
                    className={combine(styles.carouselSlide, styles.empty)}
                  >
                    <FontAwesomeIcon icon={faCameraSlash} size={'5x'} />
                  </Carousel.Item>
                ) : (
                  submission?.fields?.images?.map((media, index) => (
                    <Carousel.Item
                      key={index}
                      className={styles.carouselSlide}
                      style={{
                        backgroundImage: `url(${media.url})`,
                      }}
                    >
                      <div className={styles.fullScreenBtn}>
                        <FontAwesomeIcon
                          onClick={() => setFullscreen(media.url)}
                          icon={faExpand}
                          className={styles.iconFullScreen}
                        />
                      </div>
                    </Carousel.Item>
                  ))
                )}
              </Carousel>
            </Col>
            <Col sm={12} lg={4} className={styles.entryDescription}>
              <p className={styles.entryDescriptionInner}>
                {submission?.fields?.designStatement ||
                  submission?.fields?.statement}
              </p>
              <Row>
                <Col>
                  {(submission?.fields?.dimensionsWidth ||
                    submission?.fields?.dimensionsHeight ||
                    submission?.fields?.dimensionsDepth) && (
                    <span className={styles.detailValue}>
                      Size{' '}
                      <span className={styles.bold}>
                        {submission?.fields?.dimensionsWidth} x{' '}
                        {submission?.fields?.dimensionsHeight} x{' '}
                        {submission?.fields?.dimensionsDepth}
                      </span>
                    </span>
                  )}
                  {submission?.fields?.dimensionsWeight && (
                    <span className={styles.detailValue}>
                      Weight{' '}
                      <span className={styles.bold}>
                        {submission?.fields?.dimensionsWeight}
                      </span>
                    </span>
                  )}
                  <span className={styles.detailCompany}>
                    {submission.fields?.client_manufacturer
                      ?.map((item) => item.companyName)
                      .join(',') ||
                      submission.fields?.submittingOrganization
                        ?.map((item) => item.companyName)
                        .join(',')}
                  </span>
                </Col>
                {submission?.fields?.sizingMatrix && (
                  <Col className={styles.sizingIconWrapper}>
                    <FontAwesomeIcon
                      icon={SIZING_MATRIX_ICONS[submission.fields.sizingMatrix]}
                      size={'2x'}
                    />
                  </Col>
                )}
              </Row>
            </Col>
          </Row>
          <Row className={styles.informationRow}>
            {submission?.fields?.projectPDF?.map((pdf) => (
              <Col xs={4} sm={2}>
                <a href={pdf.url} target={'_blank'} rel={'noopener noreferrer'}>
                  <Button
                    text={t(`evaluation.buttons.file.${session?.awardType}`)}
                    size={ButtonSizes.LARGE}
                    type={ButtonTypes.SECONDARY}
                    className={styles.informationButtons}
                  />
                </a>
              </Col>
            ))}
            {submission?.fields?.url?.url && (
              <Col xs={4} sm={2}>
                <a
                  href={submission?.fields?.url.url}
                  target={'_blank'}
                  rel={'noopener noreferrer'}
                >
                  <Button
                    text={t(`evaluation.buttons.link.${session?.awardType}`)}
                    size={ButtonSizes.LARGE}
                    type={ButtonTypes.SECONDARY}
                    className={styles.informationButtons}
                  />
                </a>
              </Col>
            )}
            {submission?.fields?.video && (
              <Col xs={4} sm={2}>
                <a
                  href={submission?.fields?.video.url}
                  target={'_blank'}
                  rel={'noopener noreferrer'}
                >
                  <Button
                    text={'Video'}
                    size={ButtonSizes.LARGE}
                    type={ButtonTypes.SECONDARY}
                    className={styles.informationButtons}
                  />
                </a>
              </Col>
            )}
            <Col xs={2} sm={1} className={styles.protectedUrl}>
              {submission?.fields?.video?.username && (
                <div className={styles.protectedLabel}>
                  <span className={styles.label}>
                    {t('evaluation.file.username')}
                  </span>
                  {submission.fields.video.username}
                </div>
              )}
            </Col>
            <Col xs={2} sm={1} className={styles.protectedUrl}>
              {submission?.fields?.video?.password && (
                <div className={styles.protectedLabel}>
                  <span className={styles.label}>
                    {t('evaluation.file.password')}
                  </span>
                  {submission.fields.video.password}
                </div>
              )}
            </Col>
          </Row>
        </Container>
      </Container>
    </>
  );
};

EntryDetailHeader.propTypes = {
  submission: PropTypes.object,
  handleSubmissionUpdate: PropTypes.func,
};

EntryDetailHeader.defaultProps = {
  submission: {},
  handleSubmissionUpdate: () => {},
};

export default EntryDetailHeader;
